import React, { useState } from 'react';
import { Upload, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import './FormReferral.css';
import { ClientUploadResults } from '../components/ClientUploadResults';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.mp4,.mov,.avi,.wmv,.mkv,.webm';
const ACCEPTED_EXTENSIONS = ACCEPTED_TYPES.split(',');
const MAX_FILE_BYTES = 100 * 1024 * 1024;

const REASONS = {
  tooBig: 'This file is larger than the 100 MB limit, so it could not be uploaded.',
  badType: 'This type of file is not accepted. Please use a PDF, Word document, photo or video.',
  empty: 'This file is empty. Please check it and choose it again.',
  network: 'The connection dropped while this file was uploading. Please check your internet connection and try again.',
  serverTooBig: 'This file is too large to upload.',
  failed: 'This file could not be saved just now. Please try again.'
};

const checkFileLocally = (file) => {
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!ACCEPTED_EXTENSIONS.includes(ext)) return REASONS.badType;
  if (file.size > MAX_FILE_BYTES) return REASONS.tooBig;
  if (file.size === 0) return REASONS.empty;
  return null;
};

const FormClientUpload = () => {
  const [uploading, setUploading] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [uploadError, setUploadError] = useState('');
  const [items, setItems] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    contactEmail: '',
    uploaderName: '',
    uploaderRelationship: '',
    uploaderRelationshipOther: '',
    notes: '',
    files: null
  });

  const emptyForm = {
    firstName: '', lastName: '', contactEmail: '',
    uploaderName: '', uploaderRelationship: '', uploaderRelationshipOther: '',
    notes: '', files: null
  };

  const resolvedRelationship = () => {
    const rel = formData.uploaderRelationship;
    if (rel !== 'Other') return rel;
    const other = formData.uploaderRelationshipOther.trim();
    return other ? `Other: ${other}` : 'Other';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, files: e.target.files }));
  };

  const updateItem = (key, changes) => {
    setItems(prev => prev.map(i => (i.key === key ? { ...i, ...changes } : i)));
  };

  // One request per file, in order, so each file gets its own number and its own result
  const uploadOne = async (item) => {
    const localProblem = checkFileLocally(item.file);
    if (localProblem) return { status: 'failed', reason: localProblem, retryable: false };

    const body = new FormData();
    body.append('first_name', formData.firstName);
    body.append('last_name', formData.lastName);
    body.append('contact_email', formData.contactEmail);
    if (formData.uploaderName.trim()) body.append('uploader_name', formData.uploaderName.trim());
    if (formData.uploaderRelationship) body.append('uploader_relationship', resolvedRelationship());
    body.append('notes', formData.notes);
    body.append('file_number', String(item.number));
    body.append('files', item.file);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/client/upload`, { method: 'POST', body });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) return { status: 'done', reason: '' };
      if (response.status === 413) return { status: 'failed', reason: REASONS.serverTooBig, retryable: false };
      return { status: 'failed', reason: REASONS.failed, retryable: true };
    } catch (error) {
      return { status: 'failed', reason: REASONS.network, retryable: true };
    }
  };

  const uploadItems = async (toSend) => {
    setUploading(true);
    for (const item of toSend) {
      updateItem(item.key, { status: 'uploading', reason: '' });
      const result = await uploadOne(item);
      updateItem(item.key, result);
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    if (!formData.files || formData.files.length === 0) {
      setUploadError('Please select at least one file to upload.');
      return;
    }
    setUploadError('');
    const newItems = Array.from(formData.files).map((file, idx) => ({
      key: `${idx}-${file.name}`, number: idx + 1, file, status: 'pending', reason: ''
    }));
    setItems(newItems);
    await uploadItems(newItems);
  };

  const retryFailed = () => uploadItems(items.filter(i => i.status === 'failed' && i.retryable));

  const backToForm = () => {
    setItems(null);
    setFormData(prev => ({ ...prev, files: null }));
  };

  const resetAll = () => {
    setItems(null);
    setFormData(emptyForm);
  };

  return (
    <div className="form-referral-standalone">
      <Link to="/" className="form-back-home">
        <Home size={20} />
        <span>Back to Home</span>
      </Link>
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img src="/logo.png" alt="Clinical Minds Psychology" className="form-logo" />
          </div>
          <h1 className="form-title">Upload Client Documents</h1>
          <p className="form-subtitle">Please upload your documents here. All files are stored securely in Australia.</p>
        </div>

        <div className="form-content">
          {items ? (
            <ClientUploadResults
              items={items}
              busy={uploading}
              onRetry={retryFailed}
              onBack={backToForm}
              onReset={resetAll}
            />
          ) : (
            <form onSubmit={handleSubmit} className="referral-form">
              {formsOff && <PreviewFormNotice />}
              <div className="privacy-notice">
                <p>We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              </div>

              <div className="form-group">
                <label htmlFor="firstName">Client's first name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  data-testid="client-upload-first-name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Client's last name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  data-testid="client-upload-last-name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
                <p className="form-help" data-testid="client-upload-name-hint">
                  The person being assessed or seen (not your own name, unless that's you)
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="uploaderName">Your name (optional)</label>
                <input
                  type="text"
                  id="uploaderName"
                  name="uploaderName"
                  data-testid="client-upload-uploader-name"
                  value={formData.uploaderName}
                  onChange={handleInputChange}
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="uploaderRelationship">Your relationship to the client (optional)</label>
                <select
                  id="uploaderRelationship"
                  name="uploaderRelationship"
                  data-testid="client-upload-uploader-relationship"
                  value={formData.uploaderRelationship}
                  onChange={handleInputChange}
                  disabled={uploading}
                >
                  <option value="">Select...</option>
                  <option value="Mum">Mum</option>
                  <option value="Dad">Dad</option>
                  <option value="Carer">Carer</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Self">Self</option>
                  <option value="Other">Other</option>
                </select>
                {formData.uploaderRelationship === 'Other' && (
                  <input
                    type="text"
                    id="uploaderRelationshipOther"
                    name="uploaderRelationshipOther"
                    aria-label="Please specify your relationship to the client"
                    data-testid="client-upload-uploader-relationship-other"
                    value={formData.uploaderRelationshipOther}
                    onChange={handleInputChange}
                    disabled={uploading}
                    placeholder="Please specify"
                    style={{ marginTop: '0.5rem' }}
                  />
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Email Address *</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  data-testid="client-upload-email"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes (optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  data-testid="client-upload-notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  disabled={uploading}
                  rows={3}
                  placeholder="e.g. school report for Sarah, paediatrician letter"
                />
              </div>

              <div className="form-group">
                <label htmlFor="files">Documents *</label>
                <input
                  type="file"
                  id="files"
                  name="files"
                  data-testid="client-upload-files"
                  onChange={handleFileChange}
                  accept={ACCEPTED_TYPES}
                  disabled={uploading}
                  className="file-input"
                  multiple
                />
                <p className="form-help">
                  Accepted formats: PDF, Word, images or video (max 100MB). You can select multiple files.
                </p>
              </div>

              {uploadError && (
                <div className="error-message" data-testid="client-upload-error">
                  <p>{uploadError}</p>
                </div>
              )}

              <div className="form-footer">
                <p className="consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
                <button
                  type="submit"
                  className="btn-submit"
                  data-testid="client-upload-submit"
                  disabled={uploading || formsOff}
                >
                  {uploading ? (
                    <>
                      <span className="spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      Upload Documents
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormClientUpload;
